import { useState, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { NotebookPen, Camera, Play, Square, Trash2, Clock, Gauge } from "lucide-react";
import { ClickablePhoto } from "@/components/PhotoLightbox";

const TYPES = [
  { value: "livraison", label: "Livraison / Tournée" },
  { value: "perso", label: "Perso / Loisir" },
  { value: "pret", label: "Prêt à un tiers" },
  { value: "atelier", label: "Atelier / Entretien" },
  { value: "autre", label: "Autre" },
];
const CARBURANT = [0, 25, 50, 75, 100];
const ADBLUE = [{ v: "ok", l: "OK" }, { v: "faible", l: "Faible" }, { v: "rempli", l: "Rempli" }];

export const PhotoField = ({ label, fileId, onUploaded, testid }) => {
  const { token } = useAuth();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      onUploaded(res.data.id);
      toast.success("Photo ajoutée");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="mb-1 block">{label} *</Label>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={testid} />
      {fileId ? (
        <div className="relative inline-block">
          <img src={`${API}/files/${fileId}?auth=${token}`} alt="compteur" className="h-24 w-auto rounded-lg border object-cover" />
          <button type="button" onClick={() => inputRef.current?.click()} className="absolute bottom-1 right-1 bg-white/90 rounded-full p-1 border shadow"><Camera className="w-4 h-4 text-slate-700" /></button>
        </div>
      ) : (
        <Button type="button" variant="outline" className="w-full h-24 border-dashed" onClick={() => inputRef.current?.click()} disabled={uploading}>
          <Camera className="w-5 h-5 mr-2" />{uploading ? "Envoi…" : "Photo du compteur"}
        </Button>
      )}
    </div>
  );
};

export const CarnetRoute = ({ vehicule, onChange }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const trips = [...(vehicule.trips || [])].sort((a, b) => (b.date_depart || "").localeCompare(a.date_depart || ""));
  const openTrip = trips.find((t) => t.statut === "en_cours");

  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openForm, setOpenForm] = useState({});
  const [closeForm, setCloseForm] = useState({});

  const startOpen = () => {
    setOpenForm({ conducteur: "", type_utilisation: "livraison", prete_a: "", destination: "", km_depart: vehicule.kilometrage || 0, carburant_depart: 100, adblue_depart: "ok", remarques_depart: "", photo_depart: "" });
    setOpenDialog(true);
  };
  const startClose = () => {
    setCloseForm({ km_retour: openTrip?.km_depart || vehicule.kilometrage || 0, carburant_retour: 50, adblue_retour: "ok", remarques_retour: "", photo_retour: "" });
    setCloseDialog(true);
  };

  const submitOpen = async () => {
    if (!openForm.conducteur?.trim()) { toast.error("Nom du conducteur obligatoire"); return; }
    if (!openForm.photo_depart) { toast.error("Photo du compteur départ obligatoire"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/vehicules/${vehicule.id}/trips`, { ...openForm, km_depart: parseInt(openForm.km_depart) || 0 }, { headers });
      toast.success("Utilisation démarrée");
      setOpenDialog(false);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const submitClose = async () => {
    if (!closeForm.photo_retour) { toast.error("Photo du compteur retour obligatoire"); return; }
    if ((parseInt(closeForm.km_retour) || 0) < (openTrip?.km_depart || 0)) { toast.error(`Le km retour doit être ≥ ${openTrip?.km_depart}`); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/vehicules/${vehicule.id}/trips/${openTrip.id}/close`, { ...closeForm, km_retour: parseInt(closeForm.km_retour) || 0 }, { headers });
      toast.success("Utilisation clôturée — kilométrage mis à jour");
      setCloseDialog(false);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const removeTrip = async (id) => {
    if (!window.confirm("Supprimer cette utilisation ?")) return;
    try { await axios.delete(`${API}/vehicules/${vehicule.id}/trips/${id}`, { headers }); toast.success("Supprimé"); onChange(); }
    catch { toast.error("Erreur"); }
  };

  const typeLabel = (v) => TYPES.find((t) => t.value === v)?.label || v;
  const fmtDur = (m) => m == null ? "—" : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;

  return (
    <Card data-testid="carnet-route-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2"><NotebookPen className="w-5 h-5 text-indigo-600" />Carnet de route ({trips.length})</CardTitle>
        {openTrip ? (
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={startClose} data-testid="close-trip-btn"><Square className="w-4 h-4 mr-1" />Clôturer</Button>
        ) : (
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={startOpen} data-testid="new-trip-btn"><Play className="w-4 h-4 mr-1" />Nouvelle utilisation</Button>
        )}
      </CardHeader>
      <CardContent>
        {openTrip && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="trip-en-cours">
            <b>En cours</b> — {openTrip.conducteur} · {typeLabel(openTrip.type_utilisation)} · départ {openTrip.km_depart?.toLocaleString()} km
          </div>
        )}
        {trips.length === 0 ? (
          <p className="text-slate-400 text-sm">Aucune utilisation enregistrée.</p>
        ) : (
          <div className="space-y-2">
            {trips.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-white" data-testid={`trip-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{t.conducteur}</span>
                    <Badge variant="outline" className="text-xs">{typeLabel(t.type_utilisation)}</Badge>
                    {t.statut === "en_cours" && <Badge className="bg-amber-500 text-xs">En cours</Badge>}
                    {t.prete_a && <span className="text-xs text-slate-500">→ {t.prete_a}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{t.km_depart?.toLocaleString()}{t.km_retour != null ? ` → ${t.km_retour.toLocaleString()} km` : " km"}</span>
                    {t.km_parcourus != null && <span className="text-indigo-600 font-medium">{t.km_parcourus} km parcourus</span>}
                    {t.duree_min != null && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDur(t.duree_min)}</span>}
                    {t.cout_carburant != null && <span>≈ {t.cout_carburant.toFixed(2)} € carburant</span>}
                    {t.destination && <span className="truncate">{t.destination}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {t.photo_depart && <ClickablePhoto src={`${API}/files/${t.photo_depart}?auth=${token}`} alt="Compteur départ" className="h-10 w-10" testid={`carnet-photo-depart-${t.id || ''}`} />}
                  {t.photo_retour && <ClickablePhoto src={`${API}/files/${t.photo_retour}?auth=${token}`} alt="Compteur retour" className="h-10 w-10" testid={`carnet-photo-retour-${t.id || ''}`} />}
                  <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeTrip(t.id)} data-testid={`trip-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog Nouvelle utilisation */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="open-trip-dialog">
          <DialogHeader><DialogTitle>Nouvelle utilisation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Conducteur *</Label><Input value={openForm.conducteur || ""} onChange={(e) => setOpenForm({ ...openForm, conducteur: e.target.value })} data-testid="trip-conducteur" /></div>
            <div>
              <Label>Type d'utilisation</Label>
              <Select value={openForm.type_utilisation} onValueChange={(v) => setOpenForm({ ...openForm, type_utilisation: v })}>
                <SelectTrigger data-testid="trip-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {openForm.type_utilisation === "pret" && (
              <div><Label>Prêté à</Label><Input value={openForm.prete_a || ""} onChange={(e) => setOpenForm({ ...openForm, prete_a: e.target.value })} data-testid="trip-prete-a" /></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Km départ</Label><Input type="number" value={openForm.km_depart} onChange={(e) => setOpenForm({ ...openForm, km_depart: e.target.value })} data-testid="trip-km-depart" /></div>
              <div>
                <Label>Carburant</Label>
                <Select value={String(openForm.carburant_depart)} onValueChange={(v) => setOpenForm({ ...openForm, carburant_depart: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CARBURANT.map((c) => <SelectItem key={c} value={String(c)}>{c}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>AdBlue</Label>
                <Select value={openForm.adblue_depart} onValueChange={(v) => setOpenForm({ ...openForm, adblue_depart: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ADBLUE.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Destination</Label><Input value={openForm.destination || ""} onChange={(e) => setOpenForm({ ...openForm, destination: e.target.value })} data-testid="trip-destination" /></div>
            </div>
            <div><Label>Remarques</Label><Textarea rows={2} value={openForm.remarques_depart || ""} onChange={(e) => setOpenForm({ ...openForm, remarques_depart: e.target.value })} /></div>
            <PhotoField label="Photo compteur départ" fileId={openForm.photo_depart} onUploaded={(id) => setOpenForm({ ...openForm, photo_depart: id })} testid="trip-photo-depart" />
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={submitOpen} disabled={saving} data-testid="submit-open-trip"><Play className="w-4 h-4 mr-2" />{saving ? "…" : "Démarrer l'utilisation"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Clôturer */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="close-trip-dialog">
          <DialogHeader><DialogTitle>Clôturer l'utilisation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Départ : {openTrip?.km_depart?.toLocaleString()} km — {openTrip?.conducteur}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Km retour</Label><Input type="number" value={closeForm.km_retour} onChange={(e) => setCloseForm({ ...closeForm, km_retour: e.target.value })} data-testid="trip-km-retour" /></div>
              <div>
                <Label>Carburant</Label>
                <Select value={String(closeForm.carburant_retour)} onValueChange={(v) => setCloseForm({ ...closeForm, carburant_retour: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CARBURANT.map((c) => <SelectItem key={c} value={String(c)}>{c}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>AdBlue</Label>
              <Select value={closeForm.adblue_retour} onValueChange={(v) => setCloseForm({ ...closeForm, adblue_retour: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ADBLUE.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Remarques</Label><Textarea rows={2} value={closeForm.remarques_retour || ""} onChange={(e) => setCloseForm({ ...closeForm, remarques_retour: e.target.value })} /></div>
            <PhotoField label="Photo compteur retour" fileId={closeForm.photo_retour} onUploaded={(id) => setCloseForm({ ...closeForm, photo_retour: id })} testid="trip-photo-retour" />
            <Button className="w-full bg-amber-600 hover:bg-amber-700" onClick={submitClose} disabled={saving} data-testid="submit-close-trip"><Square className="w-4 h-4 mr-2" />{saving ? "…" : "Clôturer et mettre à jour le km"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CarnetRoute;
